// Every word the app shows, looked up by key.
//
// The text itself lives in `src/strings/en/*.json`, one file per area, written
// in ICU MessageFormat — the syntax every translation tool reads:
//
//   "streak": "{days}-day streak · {rest, plural, one {# rest day} other {# rest days}} left"
//
//   t('home.streak', { days, rest })
//
// The first segment of a key is the file; the rest is the path inside it. Run
// `npm run strings` after editing a catalog so `keys.ts` — and with it `tsc` —
// knows which keys exist and what each one needs.
//
// English is the only locale. Adding one is a sibling folder and a switch on
// `LOCALE`; nothing here assumes English beyond that constant.
//
// Kept free of the DOM, of `import.meta.env` and of `fetch`: `core/traits.ts`
// reads its wording from here, and `tools/pipeline` imports that under `tsx`.

import { IntlMessageFormat } from 'intl-messageformat';
import app from './en/app.json';
import birdle from './en/birdle.json';
import common from './en/common.json';
import home from './en/home.json';
import library from './en/library.json';
import quiz from './en/quiz.json';
import species from './en/species.json';
import stats from './en/stats.json';
import traits from './en/traits.json';
import you from './en/you.json';
import type { MessageKey, Messages } from './keys';

export type { MessageKey } from './keys';

export const LOCALE = 'en';

const catalogs: Record<string, unknown> = {
  app,
  birdle,
  common,
  home,
  library,
  quiz,
  species,
  stats,
  traits,
  you,
};

const messages = new Map<string, string>();
(function flatten(node: unknown, path: string) {
  if (typeof node === 'string') messages.set(path, node);
  else if (node && typeof node === 'object') {
    for (const [k, v] of Object.entries(node)) flatten(v, path ? `${path}.${k}` : k);
  }
})(catalogs, '');

const compiled = new Map<string, IntlMessageFormat>();

function compile(key: string): IntlMessageFormat | null {
  let fmt = compiled.get(key);
  if (!fmt) {
    const source = messages.get(key);
    if (source === undefined) return null;
    fmt = new IntlMessageFormat(source, LOCALE);
    compiled.set(key, fmt);
  }
  return fmt;
}

type ValueOf<Kind, Part, Tag> = Kind extends 'number'
  ? number
  : Kind extends 'tag'
    ? Tag
    : string | number | Part;

type Values<K extends MessageKey, Part, Tag> = {
  [A in keyof Messages[K]]: ValueOf<Messages[K][A], Part, Tag>;
};

/** No second argument for a message that takes none. */
type Args<K extends MessageKey, Part, Tag> = keyof Messages[K] extends never
  ? []
  : [values: Values<K, Part, Tag>];

/** The message at `key`, filled in, as plain text. */
export function t<K extends MessageKey>(
  key: K,
  ...args: Args<K, never, (chunks: string[]) => string>
): string {
  const fmt = compile(key);
  if (!fmt) {
    // `tsc` rules this out for literal keys; a computed one can still miss.
    console.error(`strings: no message for "${key}"`);
    return key;
  }
  const out = fmt.format(args[0] as Record<string, never>);
  return Array.isArray(out) ? out.join('') : String(out);
}

/**
 * The message at `key` as parts, for text that wraps or contains a node — a
 * bold word, a link, an icon mid-sentence. Values may be nodes, and a
 * `<b>…</b>` tag in the message is filled by the function of the same name:
 *
 *   el('p', {}, ...tParts('home.hint', { b: (c) => el('b', {}, ...c) }))
 *
 * Parts are DOM nodes unless said otherwise — as a type only, which erases, so
 * this file still runs where there is no DOM.
 */
export function tParts<K extends MessageKey, Part extends object = Node>(
  key: K,
  ...args: Args<K, Part, (chunks: (string | Part)[]) => Part>
): (string | Part)[] {
  const fmt = compile(key);
  if (!fmt) {
    console.error(`strings: no message for "${key}"`);
    return [key];
  }
  const out = fmt.format<Part>(args[0] as Record<string, never>);
  return (Array.isArray(out) ? out : [out]).map((p) =>
    typeof p === 'object' && p !== null ? (p as Part) : String(p),
  );
}

/** Whether a computed key names a message — for lookups keyed by data. */
export function hasMessage(key: string): key is MessageKey {
  return messages.has(key);
}
